import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import {
  adminClient,
  type AdminOrgBilling,
  type AdminProjectRow,
  type AdminStudioSeatsResponse,
  type AdminUsageSummary,
} from '@/api/adminClient';
import { TurnOffStudioSection } from '@/components/org/OrgStudioActions';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminMetaItem } from '@/components/shell/AdminMetaItem';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { StatTile } from '@/components/shell/StatTile';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { ORG_SPEND_DAYS, spendBreakdown, spendLabel } from '@/components/usage/OrgUsagePanel';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { billingStatus } from '@/lib/billingStatus';
import { formatCount, formatDate, formatUsd, orgLabel, orgSecondaryLabel, rangeForDays } from '@/lib/format';
import { legacyOrgTabPath, orgMoneyPath, orgSectionPath } from '@/lib/orgNav';
import { humanizeKey } from '@/lib/runTelemetry';
import { useOrgScope } from '@/pages/org/OrgLayout';

const RECENT_PROJECTS = 5;

export function OrgOverviewPage() {
  const [searchParams] = useSearchParams();
  const { orgId } = useOrgScope();
  const legacyPath = legacyOrgTabPath(orgId, searchParams);
  if (legacyPath) return <Navigate to={legacyPath} replace />;
  return <OrgOverview />;
}

function OrgOverview() {
  const { orgId, org } = useOrgScope();
  const { isPlatformAdmin } = useAdminAccess();
  const [billing, setBilling] = useState<AdminOrgBilling | undefined>();
  const [seats, setSeats] = useState<AdminStudioSeatsResponse | undefined>();
  const [spend, setSpend] = useState<AdminUsageSummary | undefined>();
  const [projects, setProjects] = useState<AdminProjectRow[] | undefined>();
  const [projectsFailed, setProjectsFailed] = useState(false);
  const [projectsReloadKey, setProjectsReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Each panel stays useful when another source fails.
    void adminClient
      .getOrgBilling(orgId)
      .then((next) => !cancelled && setBilling(next))
      .catch(() => undefined);
    void adminClient
      .listStudioSeats(orgId, { limit: 1 })
      .then((next) => !cancelled && setSeats(next))
      .catch(() => undefined);
    void adminClient
      .getOrgUsageSummary(orgId, rangeForDays(ORG_SPEND_DAYS))
      .then((next) => !cancelled && setSpend(next))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    setProjectsFailed(false);
    void adminClient
      .listProjects(orgId, { limit: RECENT_PROJECTS })
      .then((next) => !cancelled && setProjects(next.items))
      .catch(() => !cancelled && setProjectsFailed(true));
    return () => {
      cancelled = true;
    };
  }, [orgId, projectsReloadKey]);

  const pendingCount = seats?.pendingGrants.length ?? 0;
  const split = spend ? spendBreakdown(spend) : undefined;

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader title="Overview" />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Credits remaining"
          value={billing ? formatUsd((billing.creditBalanceCents ?? 0) / 100) : '—'}
          detail={billing ? `${formatUsd((billing.creditGrantedCents ?? 0) / 100)} granted` : undefined}
          to={orgMoneyPath(orgId, 'credits')}
        />
        <StatTile
          label={spendLabel(ORG_SPEND_DAYS)}
          value={split ? formatUsd(split.total) : '—'}
          detail={split ? `${formatUsd(split.aiBuilding)} AI building · ${formatUsd(split.integrations)} integrations` : undefined}
          to={orgMoneyPath(orgId, 'spend')}
        />
        <StatTile
          label="People"
          value={seats ? formatCount(seats.seatCount) : '—'}
          detail={pendingCount ? `${formatCount(pendingCount)} invited, not signed in` : 'Studio seats'}
          to={orgSectionPath(orgId, 'members')}
        />
        <StatTile
          label="Plan"
          value={billing?.productName || org.billing?.productName || 'No plan'}
          detail={billingStatus(billing?.status || org.billing?.status).label}
          to={orgMoneyPath(orgId, 'plan')}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <DataTable
            toolbar={
              <div className="flex w-full items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Recent projects</h2>
                  <p className="text-xs text-muted-foreground">{formatCount(org.projectCount)} in total</p>
                </div>
                <Link
                  to={orgSectionPath(orgId, 'projects')}
                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  View all
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </div>
            }
          >
            <DataTableHead
              columns={[{ label: 'Project' }, { label: 'Status' }, { label: 'Updated', className: 'text-right' }]}
            />
            {!projects && projectsFailed ? (
              <DataTableLoadError colSpan={3} onRetry={() => setProjectsReloadKey((key) => key + 1)} />
            ) : !projects ? (
              <DataTableMessage colSpan={3} loading>
                Loading projects
              </DataTableMessage>
            ) : projects.length === 0 ? (
              <DataTableMessage colSpan={3}>No projects yet</DataTableMessage>
            ) : (
              <tbody>
                {projects.map((project) => {
                  const to = `${orgSectionPath(orgId, 'projects')}/${encodeURIComponent(project.projectId)}`;
                  return (
                    <DataTableRow key={project.projectId} to={to}>
                      <DataTableCell>
                        <Link to={to} className="font-medium text-foreground hover:underline">
                          {project.displayName || project.title}
                        </Link>
                      </DataTableCell>
                      <DataTableCell>
                        <StatusPill tone={project.status === 'active' ? 'success' : 'neutral'}>
                          {humanizeKey(project.status)}
                        </StatusPill>
                      </DataTableCell>
                      <DataTableCell className="text-right text-muted-foreground">
                        {formatDate(project.updatedAt)}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </tbody>
            )}
          </DataTable>
        </div>

        <AdminSection title="Details">
          <dl className="grid gap-4">
            <AdminMetaItem label="Display name">{orgLabel(org)}</AdminMetaItem>
            <AdminMetaItem label="WorkOS name">{orgSecondaryLabel(org) || org.name || '—'}</AdminMetaItem>
            <AdminMetaItem label="Organization id">
              <span className="font-mono text-xs">{org.organizationId}</span>
            </AdminMetaItem>
            <AdminMetaItem label="Fabric">
              {org.fabric ? (
                <Link to={orgSectionPath(orgId, 'fabric')} className="font-mono text-xs hover:underline">
                  {org.fabric.namespace || org.fabric.fabricId}
                </Link>
              ) : (
                <Link to={orgSectionPath(orgId, 'fabric')} className="text-primary hover:underline">
                  Place on a Fabric
                </Link>
              )}
            </AdminMetaItem>
          </dl>
        </AdminSection>
      </div>

      {isPlatformAdmin ? (
        <div className="mt-6">
          <TurnOffStudioSection orgId={orgId} name={orgLabel(org)} />
        </div>
      ) : null}
    </div>
  );
}
